import React, { useState, useRef } from 'react';
import './NFSModal.css';

function NFSModal({ closeModal, addQuoteItem, formatPrice }) {
  const [quantityNFS, setQuantityNFS] = useState(1);
  const [name, setName] = useState(''); 
  const quantityInputRef = useRef(null);
  
  // Storage limits
  const storageMin = 50;
  const storageMax = 16380;
  
  const [storageNFS, setStorageNFS] = useState(storageMin);

  // Price per GiB
  const pricePerGiB = 0.3;

  // Conversion factor from GiB to GB
  const conversionFactorGiBtoGB = 1.0737;

  // Conversion factor from TiB to TB
  const conversionFactorTiBtoTB = 1.0995;

  // Conversion factor from PiB to PB
  const conversionFactorPiBtoPB = 1.1259;

  // Handle quantity change
  const handleQuantityNFSChange = (e) => {
    const value = e.target.value;
    if (value === '' || (Number(value) >= 1 && Number(value) <= 1000)) {
      setQuantityNFS(value);
    }
  };

  // Handle quantity blur
  const handleQuantityNFSBlur = () => {
    if (quantityNFS === '' || quantityNFS < 1) {
      setQuantityNFS(1);
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  };

  // Handle input change for storage NFS with validation
  const handleStorageNFSChange = (e) => {
    const value = e.target.value;

    // Allow the field to be empty
    if (value === '') {
      setStorageNFS('');
      return;
    }

    const numericValue = parseInt(value, 10);

    // Prevent negative values
    if (isNaN(numericValue) || numericValue < 0) {
      return;
    }

    // Allow values during typing if they're incomplete (1-3 digits) or within valid range
    if (value.length <= 3 || (numericValue >= storageMin && numericValue <= storageMax)) {
      setStorageNFS(numericValue);
    }
  };

  // Handle storage NFS blur with validation and rounding
  const handleStorageNFSBlur = (e) => {
    let value = parseInt(e.target.value, 10);

    // If the input is empty, reset to the minimum value
    if (isNaN(value)) {
      setStorageNFS(storageMin);
      return;
    }

    // Enforce minimum and maximum constraints with rounding to nearest 10
    if (value <= storageMin) {
      setStorageNFS(storageMin);
    } else if (value >= storageMax) {
      setStorageNFS(storageMax);
    } else {
      // Round to nearest 10
      value = Math.round(value / 10) * 10;
      // Ensure it's at least the minimum
      value = Math.max(storageMin, value);
      setStorageNFS(value);
    }
  };

  // Function to increment the storage size by 10, capped at the maximum limit
  const handleStorageIncrement = () => {
    const currentStorage = parseInt(storageNFS, 10) || storageMin;
    const newStorage = Math.min(storageMax, currentStorage + 10);
    setStorageNFS(newStorage);
  };

  // Function to decrement the storage size by 10, capped at the minimum limit
  const handleStorageDecrement = () => {
    const currentStorage = parseInt(storageNFS, 10) || storageMin;
    const newStorage = Math.max(storageMin, currentStorage - 10);
    setStorageNFS(newStorage);
  };

  const handleAddNFS = () => {
    const parsedStorageNFS = parseInt(storageNFS, 10);
    if (!isNaN(parsedStorageNFS) && parsedStorageNFS > 0 && parsedStorageNFS <= 16380) {
      const nfsDescription = `Network File Storage share of ${parsedStorageNFS.toLocaleString()} GiB (${(parsedStorageNFS * conversionFactorGiBtoGB).toLocaleString(undefined, {maximumFractionDigits: 0})} GB).`;
      let features = '';

      addQuoteItem({
        serviceType: 'Network File Storage',
        name: name, 
        description: nfsDescription,
        features: features,
        priceMonthly: formatPrice(parsedStorageNFS * pricePerGiB), 
        quantity: quantityNFS,
        totalPrice: formatPrice(parsedStorageNFS * pricePerGiB * quantityNFS)
      });
      closeModal(); 
    }
  };  

  return (
    <div id="modal" className="modal nfs-modal">
      <div id="modal-content" className="modal-content">
        <span id="close" className="close" onClick={closeModal}>&times;</span>
        <div id="modal-header" className="modal-header flex flex-between flex-center">
          <div>
            <h1>
              Add Network File Storage
            </h1>
          </div>
          <div id="modal-item-name" className="modal-item-name">
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter item name or description"
              maxLength="50" 
            />
          </div>
        </div>
        <div id="modal-body" className="modal-body">
          <div>
            <h5>SELECT NFS SHARE SIZE</h5>
            <div className="flex flex-start flex-center gap-2">
              <div className="stepper-container">
                <div className="stepper-input-wrapper">
                  <div className="stepper-input">
                    <input 
                      type="number" 
                      className="stepper-input-field" 
                      name="nfs-storage-count" 
                      placeholder="" 
                      min={storageMin}
                      max={storageMax} 
                      step="10"
                      aria-invalid="false" 
                      autoComplete="off" 
                      spellCheck="false" 
                      value={storageNFS}
                      onChange={handleStorageNFSChange}
                      onBlur={handleStorageNFSBlur}
                    />
                  </div>
                </div>
                <div className="stepper-button-wrapper-up">
                  <button 
                    className="stepper-button-increment" 
                    onClick={handleStorageIncrement}
                    disabled={storageNFS >= storageMax}
                  >
                    <svg className="stepper-icon-up" aria-hidden="true">
                      <use href="#_sprite-arrow-up"></use>
                    </svg>
                  </button>
                </div>
                <div className="stepper-button-wrapper-down">
                  <button 
                    className="stepper-button-decrement" 
                    onClick={handleStorageDecrement}
                    disabled={storageNFS <= storageMin}
                  >
                    <svg className="stepper-icon-down" aria-hidden="true">
                      <use href="#_sprite-arrow-down"></use>
                    </svg>
                  </button>
                </div>
              </div>
              <div className='font-size-dot8'>
                <p className="font-weight-600">Share Size Range</p>
                <p>{storageMin} GiB - {storageMax} GiB</p>
              </div>
            </div>
            <h5>SELECT NFS SHARE QUANTITY</h5>
            <input
              ref={quantityInputRef}
              type="number"
              className="input"
              value={quantityNFS}
              onChange={handleQuantityNFSChange}
              onBlur={handleQuantityNFSBlur}
              min="1"
              max="1000"
            />
          </div>
        </div>
        <div id="modal-footer" className="modal-footer flex flex-between flex-center">
          <div id="action-button" className="action-button">
            <button
              className="button-blue"
              onClick={handleAddNFS}
              disabled={!storageNFS || storageNFS <= 0} 
            >
              Add Network File Storage
            </button>
          </div>
          <div id="price" className="price">
            <span className="dollar dollar-large">$</span>
            <span className="cost cost-large">
              {storageNFS ? formatPrice(parseInt(storageNFS, 10) * pricePerGiB * quantityNFS) : '0'}
            </span>
            <span className="period period-large">/mo</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NFSModal;